/**
 * IndexedDB-based local store for shared contacts
 * Implements efficient caching with 15-minute incremental updates
 */

import { normalizeForSearch } from '../utils/latvianNormalizer';

export interface PhoneNumber {
  number: string;
  type: 'work' | 'mobile' | 'home';
}

export interface Contact {
  id: string;
  etag: string;
  source: string; // source user/UPN or "personal" for user's own contacts
  sourceType: 'corporate' | 'personal' | 'internal'; // distinguishes between corporate, personal, and internal contacts
  displayName: string;
  givenName: string;
  surname: string;
  emailPrimary: string;
  emails: string[];
  company: string;
  title: string;
  phones: PhoneNumber[];
  updatedAt: string;
  deleted: boolean;
  normalized: {
    nameLower: string;
    givenNameLower: string;
    surnameLower: string;
    emailLower: string;
    companyLower: string;
  };
}

export interface MetaData {
  schemaVersion: number;
  lastFullSync: string | null;
  lastIncrementalSync: string | null;
  deltaToken: string | null;
  changeToken: string | null;
  bootstrapComplete: boolean;
  totalContacts: number;
  // Personal contacts metadata
  personalLastFullSync: string | null;
  personalLastIncrementalSync: string | null;
  personalDeltaToken: string | null;
  personalBootstrapComplete: boolean;
  personalTotalContacts: number;
  // Internal users metadata
  internalLastFullSync: string | null;
  internalLastIncrementalSync: string | null;
  internalDeltaToken: string | null;
  internalBootstrapComplete: boolean;
  internalTotalUsers: number;
}

export interface SearchIndex {
  id: string;
  data: string; // JSON stringified search index blob
  lastUpdated: string;
}


export class ContactStore {
  private dbName = 'CCContactsDB';
  private dbVersion = 2;
  private db: IDBDatabase | null = null;
  private selectedContactIds: Set<string> = new Set();
  private selectionListeners: Set<() => void> = new Set();
  private contactsInEmailIds: Set<string> = new Set(); // in-memory tracking for contacts in email fields

  constructor() {
    this.initDB();
  }

  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Contacts store
        if (!db.objectStoreNames.contains('contacts')) {
          const contactsStore = db.createObjectStore('contacts', { keyPath: 'id' });
          contactsStore.createIndex('updatedAt', 'updatedAt', { unique: false });
          contactsStore.createIndex('nameLower', 'normalized.nameLower', { unique: false });
          contactsStore.createIndex('givenNameLower', 'normalized.givenNameLower', { unique: false });
          contactsStore.createIndex('surnameLower', 'normalized.surnameLower', { unique: false });
          contactsStore.createIndex('emailLower', 'normalized.emailLower', { unique: false });
          contactsStore.createIndex('companyLower', 'normalized.companyLower', { unique: false });
          contactsStore.createIndex('deleted', 'deleted', { unique: false });
          contactsStore.createIndex('sourceType', 'sourceType', { unique: false });
        }

        // Meta store
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }

        // Search index store
        if (!db.objectStoreNames.contains('index')) {
          db.createObjectStore('index', { keyPath: 'id' });
        }
      };
    });
  }

  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.initDB();
    }
    if (!this.db) {
      throw new Error('Failed to initialize IndexedDB');
    }
    return this.db;
  }

  // Meta operations
  async getMeta(): Promise<MetaData> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['meta'], 'readonly');
      const store = transaction.objectStore('meta');
      const request = store.get('main');

      request.onsuccess = () => {
        const result = request.result;
        resolve(result?.value || {
          schemaVersion: 1,
          lastFullSync: null,
          lastIncrementalSync: null,
          deltaToken: null,
          changeToken: null,
          bootstrapComplete: false,
          totalContacts: 0,
          personalLastFullSync: null,
          personalLastIncrementalSync: null,
          personalDeltaToken: null,
          personalBootstrapComplete: false,
          personalTotalContacts: 0,
        });
      };
      request.onerror = () => reject(request.error);
    });
  }

  async setMeta(meta: MetaData): Promise<void> {
    const db = await this.ensureDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['meta'], 'readwrite');
      const store = transaction.objectStore('meta');
      const request = store.put({ key: 'main', value: meta });

      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => {
        console.error('ContactStore.setMeta failed:', request.error);
        reject(request.error);
      };
    });
  }

  // Contact operations
  async getContact(id: string): Promise<Contact | null> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['contacts'], 'readonly');
      const store = transaction.objectStore('contacts');
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllContacts(): Promise<Contact[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['contacts'], 'readonly');
      const store = transaction.objectStore('contacts');
      const request = store.getAll();

      request.onsuccess = () => {
        const contacts = request.result.filter(contact => !contact.deleted);
        resolve(contacts);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getContactsBySourceType(sourceType: 'corporate' | 'personal' | 'internal'): Promise<Contact[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['contacts'], 'readonly');
      const store = transaction.objectStore('contacts');
      const index = store.index('sourceType');
      const request = index.getAll(sourceType);

      request.onsuccess = () => {
        const contacts = request.result.filter(contact => !contact.deleted);
        resolve(contacts);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async searchContacts(query: string): Promise<Contact[]> {
    const db = await this.ensureDB();
    const lowerQuery = normalizeForSearch(query);
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['contacts'], 'readonly');
      const store = transaction.objectStore('contacts');
      const results: Contact[] = [];
      let processed = 0;

      // Search by display name
      const nameIndex = store.index('nameLower');
      const nameRequest = nameIndex.openCursor();
      
      nameRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const contact = cursor.value as Contact;
          if (!contact.deleted && contact.normalized.nameLower.includes(lowerQuery)) {
            results.push(contact);
          }
          cursor.continue();
        } else {
          // Search by given name
          const givenNameIndex = store.index('givenNameLower');
          const givenNameRequest = givenNameIndex.openCursor();
          
          givenNameRequest.onsuccess = (givenNameEvent) => {
            const givenNameCursor = (givenNameEvent.target as IDBRequest).result;
            if (givenNameCursor) {
              const contact = givenNameCursor.value as Contact;
              if (!contact.deleted && 
                  contact.normalized.givenNameLower.includes(lowerQuery) &&
                  !results.find(r => r.id === contact.id)) {
                results.push(contact);
              }
              givenNameCursor.continue();
            } else {
              // Search by surname
              const surnameIndex = store.index('surnameLower');
              const surnameRequest = surnameIndex.openCursor();
              
              surnameRequest.onsuccess = (surnameEvent) => {
                const surnameCursor = (surnameEvent.target as IDBRequest).result;
                if (surnameCursor) {
                  const contact = surnameCursor.value as Contact;
                  if (!contact.deleted && 
                      contact.normalized.surnameLower.includes(lowerQuery) &&
                      !results.find(r => r.id === contact.id)) {
                    results.push(contact);
                  }
                  surnameCursor.continue();
                } else {
                  // Search by email
                  const emailIndex = store.index('emailLower');
                  const emailRequest = emailIndex.openCursor();
                  
                  emailRequest.onsuccess = (emailEvent) => {
                    const emailCursor = (emailEvent.target as IDBRequest).result;
                    if (emailCursor) {
                      const contact = emailCursor.value as Contact;
                      if (!contact.deleted && 
                          contact.normalized.emailLower.includes(lowerQuery) &&
                          !results.find(r => r.id === contact.id)) {
                        results.push(contact);
                      }
                      emailCursor.continue();
                    } else {
                      // Search by company
                      const companyIndex = store.index('companyLower');
                      const companyRequest = companyIndex.openCursor();
                      
                      companyRequest.onsuccess = (companyEvent) => {
                        const companyCursor = (companyEvent.target as IDBRequest).result;
                        if (companyCursor) {
                          const contact = companyCursor.value as Contact;
                          if (!contact.deleted && 
                              contact.normalized.companyLower.includes(lowerQuery) &&
                              !results.find(r => r.id === contact.id)) {
                            results.push(contact);
                          }
                          companyCursor.continue();
                        } else {
                          // Return unique results
                          resolve(results);
                        }
                      };
                      companyRequest.onerror = () => reject(companyRequest.error);
                    }
                  };
                  emailRequest.onerror = () => reject(emailRequest.error);
                }
              };
              surnameRequest.onerror = () => reject(surnameRequest.error);
            }
          };
          givenNameRequest.onerror = () => reject(givenNameRequest.error);
        }
      };
      nameRequest.onerror = () => reject(nameRequest.error);
    });
  }

  async bulkUpsertContacts(contacts: Contact[]): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      if (contacts.length === 0) {
        resolve();
        return;
      }

      const transaction = db.transaction(['contacts'], 'readwrite');
      const store = transaction.objectStore('contacts');

      // Use a more efficient approach: collect all promises and await them
      const promises: Promise<void>[] = [];
      
      for (const contact of contacts) {
        const promise = new Promise<void>((resolvePut, rejectPut) => {
          const request = store.put(contact);
          request.onsuccess = () => resolvePut();
          request.onerror = () => rejectPut(request.error);
        });
        promises.push(promise);
      }

      // Wait for all puts to complete
      Promise.all(promises)
        .then(() => resolve())
        .catch(reject);

      // Handle transaction errors
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async markContactDeleted(id: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['contacts'], 'readwrite');
      const store = transaction.objectStore('contacts');
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const contact = getRequest.result;
        if (contact) {
          contact.deleted = true;
          const putRequest = store.put(contact);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve();
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async bulkMarkContactsDeleted(ids: string[]): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      if (ids.length === 0) {
        resolve();
        return;
      }

      const transaction = db.transaction(['contacts'], 'readwrite');
      const store = transaction.objectStore('contacts');

      const promises: Promise<void>[] = [];
      
      for (const id of ids) {
        const promise = new Promise<void>((resolveDelete, rejectDelete) => {
          const getRequest = store.get(id);
          getRequest.onsuccess = () => {
            const contact = getRequest.result;
            if (contact) {
              contact.deleted = true;
              const putRequest = store.put(contact);
              putRequest.onsuccess = () => resolveDelete();
              putRequest.onerror = () => rejectDelete(putRequest.error);
            } else {
              resolveDelete();
            }
          };
          getRequest.onerror = () => rejectDelete(getRequest.error);
        });
        promises.push(promise);
      }

      Promise.all(promises)
        .then(() => resolve())
        .catch(reject);

      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getContactCount(): Promise<number> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['contacts'], 'readonly');
      const store = transaction.objectStore('contacts');
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Search index operations
  async getSearchIndex(): Promise<SearchIndex | null> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['index'], 'readonly');
      const store = transaction.objectStore('index');
      const request = store.get('main');

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async setSearchIndex(indexData: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['index'], 'readwrite');
      const store = transaction.objectStore('index');
      const index: SearchIndex = {
        id: 'main',
        data: indexData,
        lastUpdated: new Date().toISOString(),
      };
      const request = store.put(index);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Utility methods
  normalizeContact(contact: any, source: string, sourceType: 'corporate' | 'personal' | 'internal' = 'corporate'): Contact {
    const displayName = contact.displayName || 
      `${contact.givenName || ''} ${contact.surname || ''}`.trim() || 
      'Unknown Name';
    
    const givenName = contact.givenName || '';
    const surname = contact.surname || '';
    
    // For internal users, use 'mail' field only (no UPN fallback)
    const emailPrimary = sourceType === 'internal' 
      ? contact.mail || ''
      : contact.emailAddresses?.[0]?.address || '';
    
    const emails = sourceType === 'internal'
      ? contact.mail ? [contact.mail] : []
      : contact.emailAddresses?.map((e: any) => e.address) || [];
    
    
    const company = sourceType === 'internal' 
      ? contact.companyName || contact.officeLocation || ''
      : contact.companyName || contact.department || '';

    const title = sourceType === 'internal'
      ? `${contact.department || ''} - ${contact.jobTitle || ''}`.replace(/^ - | - $|^ - $/, '')
      : contact.jobTitle || '';
    
    const phones: PhoneNumber[] = [
      ...(contact.businessPhones || []).map((number: string) => ({ number, type: 'work' as const })),
      ...(contact.mobilePhone ? [{ number: contact.mobilePhone, type: 'mobile' as const }] : [])
    ];

    return {
      id: contact.id,
      etag: contact['@odata.etag'] || contact.etag || '',
      source,
      sourceType,
      displayName,
      givenName,
      surname,
      emailPrimary,
      emails,
      company,
      title,
      phones,
      updatedAt: contact.lastModifiedDateTime || new Date().toISOString(),
      deleted: false,
      normalized: {
        nameLower: normalizeForSearch(displayName),
        givenNameLower: normalizeForSearch(givenName),
        surnameLower: normalizeForSearch(surname),
        emailLower: normalizeForSearch(emailPrimary),
        companyLower: normalizeForSearch(company),
      },
    };
  }

  async clearAllData(): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['contacts', 'meta', 'index'], 'readwrite');
      
      const contactsStore = transaction.objectStore('contacts');
      const metaStore = transaction.objectStore('meta');
      const indexStore = transaction.objectStore('index');

      let completed = 0;
      const total = 3;

      const checkComplete = () => {
        completed++;
        if (completed === total) {
          resolve();
        }
      };

      contactsStore.clear().onsuccess = checkComplete;
      metaStore.clear().onsuccess = checkComplete;
      indexStore.clear().onsuccess = checkComplete;

      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Selection management methods
  getSelectedContactIds(): string[] {
    return Array.from(this.selectedContactIds);
  }

  isContactSelected(contactId: string): boolean {
    return this.selectedContactIds.has(contactId);
  }

  toggleContactSelection(contactId: string): void {
    if (this.selectedContactIds.has(contactId)) {
      this.selectedContactIds.delete(contactId);
    } else {
      this.selectedContactIds.add(contactId);
    }
    this.notifySelectionListeners();
  }

  selectContact(contactId: string): void {
    this.selectedContactIds.add(contactId);
    this.notifySelectionListeners();
  }

  deselectContact(contactId: string): void {
    this.selectedContactIds.delete(contactId);
    this.notifySelectionListeners();
  }

  clearSelection(): void {
    this.selectedContactIds.clear();
    this.notifySelectionListeners();
  }

  getSelectedContactsCount(): number {
    return this.selectedContactIds.size;
  }

  addSelectionListener(listener: () => void): void {
    this.selectionListeners.add(listener);
  }

  removeSelectionListener(listener: () => void): void {
    this.selectionListeners.delete(listener);
  }

  private notifySelectionListeners(): void {
    this.selectionListeners.forEach(listener => listener());
  }

  async getSelectedContacts(): Promise<Contact[]> {
    const selectedIds = this.getSelectedContactIds();
    if (selectedIds.length === 0) {
      return [];
    }

    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['contacts'], 'readonly');
      const store = transaction.objectStore('contacts');
      const contacts: Contact[] = [];
      let processed = 0;

      if (selectedIds.length === 0) {
        resolve(contacts);
        return;
      }

      selectedIds.forEach(id => {
        const request = store.get(id);
        request.onsuccess = () => {
          const contact = request.result;
          if (contact && !contact.deleted) {
            contacts.push(contact);
          }
          processed++;
          if (processed === selectedIds.length) {
            resolve(contacts);
          }
        };
        request.onerror = () => {
          processed++;
          if (processed === selectedIds.length) {
            resolve(contacts);
          }
        };
      });

      transaction.onerror = () => reject(transaction.error);
    });
  }

  // Email field tracking methods (in-memory only)
  markContactsInEmail(contactIds: string[]): void {
    contactIds.forEach(id => this.contactsInEmailIds.add(id));
  }

  markContactsNotInEmail(contactIds: string[]): void {
    contactIds.forEach(id => this.contactsInEmailIds.delete(id));
  }

  isContactInEmail(contact: Contact): boolean {
    return this.contactsInEmailIds.has(contact.id);
  }

  clearEmailState(): void {
    this.contactsInEmailIds.clear();
  }

  // Find contacts by email addresses
  async findContactsByEmails(emailAddresses: string[]): Promise<Contact[]> {
    if (emailAddresses.length === 0) {
      return [];
    }

    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['contacts'], 'readonly');
      const store = transaction.objectStore('contacts');
      const foundContacts: Contact[] = [];
      let processed = 0;

      // Convert email addresses to lowercase for comparison
      const lowerEmails = emailAddresses.map(email => email.toLowerCase());

      // Get all contacts and filter by email
      const request = store.getAll();
      request.onsuccess = () => {
        const allContacts = request.result.filter(contact => !contact.deleted);
        
        allContacts.forEach(contact => {
          // Check if any of the contact's emails match the provided email addresses
          const contactEmails = [
            contact.emailPrimary,
            ...contact.emails
          ].map(email => email.toLowerCase()).filter(email => email);
          
          const hasMatchingEmail = contactEmails.some(contactEmail => 
            lowerEmails.includes(contactEmail)
          );
          
          if (hasMatchingEmail) {
            foundContacts.push(contact);
          }
        });
        
        resolve(foundContacts);
      };
      request.onerror = () => reject(request.error);
    });
  }
}
